import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

import { useSnackbar } from "notistack";

import { useGlobalModals } from "../../contexts/GlobalModalsContext";
import PageHeader from "../../components/common/PageHeader";
import FilterBar from "../../components/common/FilterBar";
import DataTableWrapper from "../../components/common/DataTableWrapper";
import EmptyState from "../../components/common/EmptyState";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import FormModal from "../../components/common/FormModal";
import ViewModal from "../../components/common/ViewModal";

import { skipToken } from "@reduxjs/toolkit/query/react";
import {
  useCreateClientMutation,
  useGetClientQuery,
  useListClientsQuery,
  useUpdateClientMutation,
  useArchiveClientMutation,
  useUnarchiveClientMutation,
  useDeleteClientMutation,
  useBulkDeleteArchivedClientsMutation,
} from "../../features/masterdata/masterdataApi";
import type { Client, ClientCreate, ClientType } from "../../features/masterdata/types";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { usePersistedListFilters } from "../../hooks/usePersistedListFilters";

const CLIENT_TYPES: { value: ClientType; label: string }[] = [
  { value: "individual", label: "Individual" },
  { value: "hotel", label: "Hotel" },
  { value: "hotel_group", label: "Hotel group" },
  { value: "company", label: "Company" },
];

function typeLabel(t: string) {
  return CLIENT_TYPES.find((x) => x.value === t)?.label ?? t;
}

type ClientOrdering = "name" | "-name" | "created_at" | "-created_at";

export default function ClientsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { showError, showConfirm } = useGlobalModals();

  const [filters, setFilters] = usePersistedListFilters("clients", {
    type: "" as ClientType | "",
    search: "",
    ordering: "name" as ClientOrdering,
    includeArchived: false,
  });
  const { type, search, ordering, includeArchived } = filters;
  const debouncedSearch = useDebouncedValue(search);

  const [page, setPage] = useState(1);

  const toggleSort = (field: "name" | "created_at") => {
    setFilters((prev) => ({
      ...prev,
      ordering: (prev.ordering === field ? `-${field}` : prev.ordering === `-${field}` ? field : field) as ClientOrdering,
    }));
  };
  const sortIcon = (field: "name" | "created_at") => {
    if (ordering === field) return " ↑";
    if (ordering === `-${field}`) return " ↓";
    return " ⇅";
  };
  const sortAria = (field: "name" | "created_at"): "ascending" | "descending" | "none" =>
    ordering === field ? "ascending" : ordering === `-${field}` ? "descending" : "none";

  // dialogs state
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);

  const [openView, setOpenView] = useState(false);
  const [viewing, setViewing] = useState<Client | null>(null);

  const [openDelete, setOpenDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Client | null>(null);

  const [archiveClient, { isLoading: isArchiving }] = useArchiveClientMutation();
  const [unarchiveClient, { isLoading: isUnarchiving }] = useUnarchiveClientMutation();

  const [form, setForm] = useState<ClientCreate>({
    type: "hotel",
    name: "",
    contact_name: null,
    email: null,
    phone: null,
    notes: null,
  });
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [type, debouncedSearch, ordering, includeArchived]);

  const { data: editData, isLoading: isLoadingEdit } = useGetClientQuery(
    editing ? editing.id : skipToken
  );

  const { data, isLoading, isFetching, error, refetch } = useListClientsQuery({
    type: type || undefined,
    search: debouncedSearch,
    ordering,
    page,
    include_archived: includeArchived || undefined,
  });

  const rows = data?.results ?? [];
  const count = data?.count ?? 0;

  const canPrev = !!data?.previous && page > 1;
  const canNext = !!data?.next;

  const [createClient, { isLoading: isCreating }] = useCreateClientMutation();
  const [updateClient, { isLoading: isUpdating }] = useUpdateClientMutation();
  const [deleteClient, { isLoading: isDeleting }] = useDeleteClientMutation();
  const [bulkDeleteArchivedClients, { isLoading: isBulkDeleting }] = useBulkDeleteArchivedClientsMutation();

  const [openBulkDelete, setOpenBulkDelete] = useState(false);

  const saving = isCreating || isUpdating;

  const showEmpty = !isLoading && !error && rows.length === 0;

  // When editing, sync form from GET-by-id so we have latest data
  useEffect(() => {
    if (editing?.id && editData?.id === editing.id) {
      setForm({
        type: editData.type,
        name: editData.name ?? "",
        contact_name: editData.contact_name ?? null,
        email: editData.email ?? null,
        phone: editData.phone ?? null,
        notes: editData.notes ?? null,
      });
    }
  }, [editing?.id, editData]);

  const onOpenCreate = () => {
    setEditing(null);
    setSubmitAttempted(false);
    setForm({
      type: "hotel",
      name: "",
      contact_name: null,
      email: null,
      phone: null,
      notes: null,
    });
    setOpenForm(true);
  };

  const onOpenEdit = (c: Client) => {
    setSubmitAttempted(false);
    setEditing(c);
    setForm({
      type: c.type,
      name: c.name ?? "",
      contact_name: c.contact_name ?? null,
      email: c.email ?? null,
      phone: c.phone ?? null,
      notes: c.notes ?? null,
    });
    setOpenForm(true);
  };

  const onOpenView = (c: Client) => {
    setViewing(c);
    setOpenView(true);
  };

  const onArchive = async (c: Client) => {
    const confirmed = await showConfirm({
      title: "Archive client?",
      message: `"${c.name}" will be archived and hidden from the default list. You can still include archived items when needed.`,
      confirmText: "Archive",
      cancelText: "Cancel",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await archiveClient({ id: c.id }).unwrap();
      enqueueSnackbar("Client archived", { variant: "success" });
    } catch (e: any) {
      const msg = e?.data?.detail || "Failed to archive client";
      enqueueSnackbar(msg, { variant: "error" });
      showError({ title: "Archive failed", message: typeof msg === "string" ? msg : "Could not archive client.", details: e?.data ? JSON.stringify(e.data, null, 2) : undefined });
    }
  };

  const onUnarchive = async (c: Client) => {
    try {
      await unarchiveClient({ id: c.id }).unwrap();
      enqueueSnackbar("Client restored", { variant: "success" });
    } catch (e: any) {
      const msg = e?.data?.detail || "Failed to restore client";
      enqueueSnackbar(msg, { variant: "error" });
      showError({ title: "Unarchive failed", message: typeof msg === "string" ? msg : "Could not restore client.", details: e?.data ? JSON.stringify(e.data, null, 2) : undefined });
    }
  };

  const onSubmitForm = async () => {
    setSubmitAttempted(true);
    if (!form.name.trim()) return;
    try {

      if (!editing) {
        await createClient({
          ...form,
          name: form.name.trim(),
          contact_name: form.contact_name || null,
          email: form.email || null,
          phone: form.phone || null,
          notes: form.notes || null,
        }).unwrap();
        enqueueSnackbar("Client created", { variant: "success" });
      } else {
        await updateClient({
          id: editing.id,
          patch: {
            ...form,
            name: form.name.trim(),
            contact_name: form.contact_name || null,
            email: form.email || null,
            phone: form.phone || null,
            notes: form.notes || null,
          },
        }).unwrap();
        enqueueSnackbar("Client updated", { variant: "success" });
      }

      setOpenForm(false);
      setEditing(null);
      setSubmitAttempted(false);
      setPage(1);
    } catch (e: any) {
      const msg = e?.data?.detail || "Failed to save client";
      enqueueSnackbar(msg, { variant: "error" });
      showError({ title: "Save failed", message: typeof msg === "string" ? msg : "Could not save client.", details: e?.data ? JSON.stringify(e.data, null, 2) : undefined });
    }
  };

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteClient({ id: deleteTarget.id }).unwrap();
      setOpenDelete(false);
      setDeleteTarget(null);
      enqueueSnackbar("Client deleted", { variant: "success" });
    } catch (e: any) {
      const msg = e?.data?.detail || "Failed to delete client";
      enqueueSnackbar(msg, { variant: "error" });
      showError({ title: "Delete failed", message: typeof msg === "string" ? msg : "Could not delete client.", details: e?.data ? JSON.stringify(e.data, null, 2) : undefined });
    }
  };

  const onConfirmBulkDelete = async () => {
    try {
      const res = await bulkDeleteArchivedClients().unwrap();
      setOpenBulkDelete(false);
      let msg = `Deleted ${res.deleted} archived client(s)`;
      if (res.skipped > 0) msg += `. ${res.skipped} skipped (referenced).`;
      if (res.reason) msg += ` ${res.reason}`;
      enqueueSnackbar(msg, { variant: "success" });
    } catch (e: any) {
      const data = e?.data;
      const msg = data?.reason || data?.detail || "Failed to delete archived clients";
      enqueueSnackbar(msg, { variant: "error" });
      const details = data?.skippedIds?.length ? `Skipped IDs: ${data.skippedIds.join(", ")}` : (data ? JSON.stringify(data, null, 2) : undefined);
      showError({ title: "Bulk delete failed", message: typeof msg === "string" ? msg : "Could not delete archived clients.", details });
    }
  };

  const headerRight = useMemo(() => {
    return (
      <Box sx={{ display: "flex", gap: 1 }}>
        {includeArchived && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => setOpenBulkDelete(true)}
            disabled={isBulkDeleting}
          >
            {isBulkDeleting ? "Deleting…" : "Bulk delete archived"}
          </Button>
        )}
        <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={onOpenCreate}>
          New Client
        </Button>
      </Box>
    );
  }, [includeArchived, isBulkDeleting]);

  return (
    <Box>
      <PageHeader
        title="Clients"
        subtitle="Hotels, hotel groups, companies, individuals"
        actions={headerRight}
      />

      <FilterBar loading={isLoading || isFetching}>
        <TextField
          label="Type"
          select
          value={type}
          onChange={(e) => setFilters({ type: e.target.value as ClientType | "" })}
          size="small"
          sx={{ minWidth: { xs: 0, sm: 180 }, width: { xs: "100%", sm: "auto" } }}
        >
          <MenuItem value="">All</MenuItem>
          {CLIENT_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Search (name, email, contact)"
          value={search}
          onChange={(e) => setFilters({ search: e.target.value })}
          size="small"
          placeholder="Search…"
          sx={{ minWidth: 0, flex: 1, width: { xs: "100%", sm: "auto" } }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={!!includeArchived}
              onChange={(e) => setFilters({ includeArchived: e.target.checked })}
            />
          }
          label="Archived only"
        />
      </FilterBar>

      {showEmpty ? (
        <EmptyState
          title="No clients yet"
          description="Create clients to link transfers and operations."
          actionLabel="Create client"
          onAction={onOpenCreate}
        />
      ) : (
        <>
          <DataTableWrapper
            loading={isLoading}
            error={!!error}
            errorMessage="Failed to load clients."
            onRetry={() => refetch()}
            minWidth={560}
          >
            <Table sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell aria-sort={sortAria("name")}>
                    <Box
                      component="span"
                      onClick={() => toggleSort("name")}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "pointer",
                        fontWeight: 600,
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      Name{sortIcon("name")}
                    </Box>
                  </TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                      <TableCell sx={{ fontWeight: 700 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {r.name}
                        {r.is_archived && <Chip size="small" label="Archived" color="default" variant="outlined" />}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={typeLabel(r.type)} />
                    </TableCell>

                    <TableCell>{r.contact_name || "-"}</TableCell>
                    <TableCell>{r.email || "-"}</TableCell>
                    <TableCell>{r.phone || "-"}</TableCell>

                    <TableCell align="right">
                      <IconButton size="small" onClick={() => onOpenView(r)} aria-label="view">
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => onOpenEdit(r)} aria-label="edit">
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      {!r.is_archived && (
                        <Tooltip title="Archive">
                          <IconButton
                            size="small"
                            onClick={() => onArchive(r)}
                            disabled={isArchiving}
                            aria-label="Archive client"
                          >
                            <ArchiveOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {r.is_archived && (
                        <>
                          <Tooltip title="Restore (unarchive)">
                            <IconButton
                              size="small"
                              onClick={() => onUnarchive(r)}
                              disabled={isUnarchiving}
                              aria-label="Unarchive client"
                            >
                              <UnarchiveOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete permanently">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setDeleteTarget(r);
                                  setOpenDelete(true);
                                }}
                                disabled={isDeleting}
                                aria-label="Delete client"
                                color="error"
                              >
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DataTableWrapper>

          {/* paging */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              Total: {count} • Page: {page}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={isFetching || !canPrev}
              >
                Prev
              </Button>
              <Button onClick={() => setPage((p) => p + 1)} disabled={isFetching || !canNext}>
                Next
              </Button>
            </Stack>
          </Stack>
        </>
      )}

      <ViewModal
        open={openView}
        onClose={() => setOpenView(false)}
        title="Client details"
        subtitle={viewing ? `${viewing.name} · ${typeLabel(viewing.type)}` : undefined}
        rows={
          viewing
            ? [
                { label: "Contact", value: viewing.contact_name },
                { label: "Email", value: viewing.email },
                { label: "Phone", value: viewing.phone },
                { label: "Notes", value: viewing.notes },
              ]
            : undefined
        }
        onEdit={
          viewing
            ? () => {
                onOpenEdit(viewing);
                setOpenView(false);
              }
            : undefined
        }
      />

      <FormModal
        open={openForm}
        onClose={() => { setSubmitAttempted(false); setOpenForm(false); }}
        title={editing ? "Edit client" : "New client"}
        onSubmit={onSubmitForm}
        submitDisabled={!form.name.trim()}
        loading={saving || (!!editing && isLoadingEdit)}
        maxWidth="sm"
      >
        <Stack spacing={2} mt={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Type"
              select
              value={form.type}
              onChange={(e) => setForm((p) => ({ ...p, type: e.target.value as ClientType }))}
              fullWidth
            >
              {CLIENT_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Company or person name"
              required
              autoFocus
              fullWidth
              error={submitAttempted && !form.name.trim()}
              helperText={submitAttempted && !form.name.trim() ? "Name is required." : undefined}
            />
          </Stack>
          <TextField
            label="Contact name (optional)"
            value={form.contact_name ?? ""}
            onChange={(e) => setForm((p) => ({ ...p, contact_name: e.target.value || null }))}
            placeholder="Primary contact"
            fullWidth
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Email (optional)"
              type="email"
              value={form.email ?? ""}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value || null }))}
              placeholder="email@example.com"
              fullWidth
            />
            <TextField
              label="Phone (optional)"
              value={form.phone ?? ""}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value || null }))}
              placeholder="+30 …"
              fullWidth
            />
          </Stack>
          <TextField
            label="Notes (optional)"
            value={form.notes ?? ""}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value || null }))}
            placeholder="Internal notes"
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </FormModal>

      {/* Delete confirm */}
      <ConfirmDialog
        open={openDelete}
        title="Delete client?"
        description={deleteTarget ? `This will permanently delete "${deleteTarget.name}".` : undefined}
        confirmText="Delete"
        severity="destructive"
        loading={isDeleting}
        onClose={() => {
          setOpenDelete(false);
          setDeleteTarget(null);
        }}
        onConfirm={onConfirmDelete}
      />
      <ConfirmDialog
        open={openBulkDelete}
        title="Delete all archived clients?"
        description="This will permanently delete all archived clients. This cannot be undone."
        confirmText="Delete all"
        severity="destructive"
        loading={isBulkDeleting}
        onClose={() => setOpenBulkDelete(false)}
        onConfirm={onConfirmBulkDelete}
      />
    </Box>
  );
}
